import { useState, useEffect } from 'react';
import styles from './CatalogPage.module.scss';

import Search from '../../sections/Search/Search';
import Filters from './components/Filters/Filters';
import List from './components/List/List';
import Pagination from './components/Pagination/Pagination';
import Empty from './components/Empty/Empty';
import BackButton from '../../BackButton/BackButton';

import { filtersData } from '../../../data/mock/catalogData';
import { useGetBrands } from '../../../queries/brands/useGetBrands';
import { useGetCategories } from '../../../queries/categories/useGetCategories';
import { useTypedSelector } from '../../../lib/hooks/useTypedSelector';
import { useGetSearchDataFull } from '../../../queries/search/useGetSearchDataFull';
import { useActions } from '../../../lib/hooks/useActions';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  IGetSearchDataFullResponsePart,
  IGetSearchDataFullResponseProduct,
} from '../../../types/search';
import Print from '../ProductPage/Print';
import { Container } from '../../Container/Container';
import { getFavorites } from '../../../helpers/favorites';
import { ICardProps as IFavorites } from '../FavoritesPage/components/Card/Card';

const CatalogPage = () => {
  const location = useLocation();

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(20);
  const { brandsData } = useGetBrands({
    'where[isActive][equals]': 'true',
  });
  const { categoriesData } = useGetCategories({ limit: 999 });
  const { brand, category, search, type } = useTypedSelector(
    (state) => state.search,
  );
  const {
    isGetSearchDataFullSuccess,
    searchDataFull,
    searchDataFullMutate,
    isGetSearchDataFullPending,
  } = useGetSearchDataFull();
  const { setFilter } = useActions();
  const [, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (search) {
      searchDataFullMutate({
        search,
        params: {
          brand,
          category,
          type,
          limit,
          page: currentPage,
        },
      });
    }
  }, [search, brand, category, type, limit, currentPage]);

  const updatePage = (page: number) => {
    setCurrentPage(page);
  };

  const updateLimit = (value: number) => {
    setLimit(value);
  };

  const updateFilters = (filters: {
    type: string;
    category: string;
    brand: string;
  }) => {
    setFilter({ key: 'type', value: filters.type });
    setFilter({ key: 'brand', value: filters.brand });
    setFilter({ key: 'category', value: filters.category });

    setCurrentPage(1);
  };

  const onSearch = (value: string) => {
    setSearchParams({
      search: value,
    });
    setFilter({ key: 'search', value });
    setCurrentPage(1);
  };

  const onSearchReset = () => {
    setFilter({ key: 'search', value: '' });
  };

  const filters = [
    filtersData[0],
    {
      name: 'brand',
      label: 'Бренд',
      options:
        brandsData?.docs.map((item) => ({
          name: item.id,
          label: item.name,
        })) || [],
    },
    {
      name: 'category',
      label: 'Категория',
      options:
        categoriesData?.docs.map((item) => ({
          name: item.id,
          label: item.title,
        })) || [],
      active: location?.state?.category ? location?.state?.category : null,
    },
  ];

  const [favorites, setFavorites] = useState<IFavorites[]>([]);

  useEffect(() => {
    window.history.replaceState({}, '');
    getFavorites().then((arr) => setFavorites(arr));
  }, []);

  return (
    <div className={styles.CatalogPage}>
      <Container>
        <div className={styles.back}>
          <BackButton />
        </div>
        <h1>Каталог продукции</h1>
        <div className={styles.controls}>
          <Search onSearch={onSearch} isFullSearch onReset={onSearchReset} />
          {brandsData && categoriesData && (
            <Filters data={filters} updateFilters={updateFilters} />
          )}
        </div>
        {!isGetSearchDataFullPending && (
          <>
            {isGetSearchDataFullSuccess &&
            searchDataFull &&
            searchDataFull.docs.length > 0 ? (
              <>
                <List
                  data={{
                    items: searchDataFull.docs.map((item) => {
                      if (item.collection === 'products') {
                        const product =
                          item as IGetSearchDataFullResponseProduct;

                        return {
                          replacedBy: null,
                          article_number: product.cmmf,
                          cmmf: product.cmmf,
                          brand: product.brand,
                          category: product.category,
                          commercial_number: product.commercialCode,
                          image: product.image,
                          in_stoke: false,
                          name: product.name,
                          type: 'product',
                          model: product.modelCode,
                          status: null,
                          id: product.id,
                          slug: product.slug,
                        };
                      }

                      const part = item as IGetSearchDataFullResponsePart;

                      return {
                        replaceBy: part.replacedBy,
                        article_number: part.cmmf,
                        cmmf: part.cmmf,
                        brand: { name: '-' },
                        category: { name: '-' },
                        commercial_number: part.partCode,
                        image: part.image,
                        in_stoke: part.status || part.status !== 'discontinued',
                        name: part.name,
                        type: part.isExtra ? 'accessory' : 'part',
                        model: '-',
                        status: part.status || null,
                        count: part.metaData.count || null,
                        id: part.id,
                        slug: part.slug,
                      };
                    }),
                    showCount: `${searchDataFull.pagingCounter}-${Math.min(
                      searchDataFull.pagingCounter + limit - 1,
                      searchDataFull.totalDocs,
                    )}`,
                    count: searchDataFull.totalDocs,
                    favorites: favorites,
                  }}
                />
                <Pagination
                  count={searchDataFull.totalPages}
                  currentPage={searchDataFull.page}
                  updatePage={updatePage}
                  updateLimit={updateLimit}
                  showMore={() => setCurrentPage((prev) => prev + 1)}
                  remainder={
                    searchDataFull.hasNextPage ? searchDataFull.totalDocs : 0
                  }
                  shoeMoreCount={
                    searchDataFull.hasNextPage
                      ? searchDataFull.totalDocs - searchDataFull.page * limit
                      : 0
                  }
                  limit={limit}
                />
                <div className={styles.cover}>
                  <p className={styles.coverText}>
                    Инструменты SEB для ремонта и сервисного обслуживания
                  </p>
                  <a
                    href="https://6e8ee6a6-c00b-40bb-a4ec-44a1c22bfeef.selstorage.ru/5aaa669e-9d46-4d24-a023-a3296c90bbb2.pdf"
                    target="_blank"
                    rel="noreferrer"
                    className={styles.coverLink}
                  >
                    <Print />
                    <span>Каталог.pdf</span> (672 КБ)
                  </a>
                </div>
              </>
            ) : (
              <Empty />
            )}
          </>
        )}
      </Container>
    </div>
  );
};

export default CatalogPage;
